import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  initializePreviewPhysics,
  disablePreviewPhysics,
  applyPhysicsToObject,
  removePhysicsFromObject,
  updatePhysicsWorld,
  syncPhysicsWithMeshes,
  hasPhysics,
  updateObjectPhysics,
} from '../utils/Physics/PhysicsUtils';
import {
  setPreviewModePhysicsEnabled,
  clearPreviewModePhysics,
} from '../redux/Slice/physicsSlice';

/**
 * Custom hook for managing physics in the application
 * @param {Object} scene - Three.js scene
 * @param {boolean} isPreviewMode - Whether we're in preview mode
 */
const usePhysics = (scene, isPreviewMode = false) => {
  const dispatch = useDispatch();
  const { physicsObjects, previewModePhysics } = useSelector(
    (state) => state.physics
  );
  
  const { select_object } = useSelector((state) => state.assets_object);

  const physicsInitialized = useRef(false);
  const animationId = useRef(null);
  const previousPreviewMode = useRef(isPreviewMode);

  // Initialize physics ONLY when entering preview mode
  useEffect(() => {
    if (isPreviewMode && !physicsInitialized.current) {
      initializePreviewPhysics();
      physicsInitialized.current = true;
      
    } else if (!isPreviewMode && physicsInitialized.current) {
      // Disable physics when exiting preview mode
      disablePreviewPhysics();
      physicsInitialized.current = false;
      
    }
  }, [isPreviewMode]);

  // Apply physics to objects ONLY when in preview mode
  useEffect(() => {
    // Keep transition tracker in sync when leaving preview mode.
    if (!isPreviewMode) {
      previousPreviewMode.current = false;
      return;
    }
    if (!scene) return;

    // Detect when entering preview mode (transitioning from design to preview)
    const wasInPreview = previousPreviewMode.current;
    const isEnteringPreview =
      isPreviewMode && !wasInPreview && physicsInitialized.current;

    // Update the previous preview mode state for next render
    previousPreviewMode.current = isPreviewMode;

    physicsObjects.forEach((physicsObject) => {
      const { objectId } = physicsObject;

      // Try multiple ways to find the object in the scene
      let mesh = scene.getObjectByName(objectId);
      

      // If not found by name, try other methods
      if (!mesh) {
        // Try to find by traversing the scene
        scene.traverse((child) => {
          if (
            !mesh &&
            (child.name === objectId ||
              child._id === objectId ||
              child.url === objectId ||
              child.userData?.id === objectId ||
              child.userData?._id === objectId ||
              child.userData?.temp_file_name === objectId)
          ) {
            mesh = child;
          }
        });
      }

      // Apply physics if:
      // 1. Mesh exists AND physics not yet applied, OR
      // 2. Entering preview mode (re-apply to ensure physics is active)
      const alreadyApplied = hasPhysics(objectId);

      if (mesh) {
        if (isEnteringPreview) {
          // When entering preview mode, ensure physics is active for all objects
          if (alreadyApplied) {
            updateObjectPhysics(objectId, physicsObject);
          } else {
            applyPhysicsToObject(objectId, mesh, physicsObject);
          }
        } else if (alreadyApplied) {
        } else {
          applyPhysicsToObject(objectId, mesh, physicsObject);
        }
      } else if (!mesh) {
        
      } else {
      }
    });
    // Depend only on source physics definitions; preview objects are an output of this effect.
  }, [physicsObjects, isPreviewMode, scene]);

  // Physics animation loop - ONLY run in preview mode
  useEffect(() => {
    // Only run physics simulation in preview mode
    if (!isPreviewMode) {
      if (animationId.current) {
        cancelAnimationFrame(animationId.current);
        animationId.current = null;
      }
      return;
    }

    let lastTime = 0;

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000; // Convert to seconds
      lastTime = currentTime;

      // Update physics world
      updatePhysicsWorld(deltaTime);

      // Sync physics with meshes
      syncPhysicsWithMeshes();

      animationId.current = requestAnimationFrame(animate);
    };

    animationId.current = requestAnimationFrame(animate);

    return () => {
      if (animationId.current) {
        cancelAnimationFrame(animationId.current);
        animationId.current = null;
      }
    };
  }, [isPreviewMode, physicsObjects]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationId.current) {
        cancelAnimationFrame(animationId.current);
      }
      if (physicsInitialized.current) {
        disablePreviewPhysics();
      }
    };
  }, []);

  return {
    isPhysicsEnabled: previewModePhysics.enabled,
    physicsObjects: previewModePhysics.objects,
    physicsInitialized: physicsInitialized.current,
  };
};

export default usePhysics;
